import React, { useState } from 'react';
import './Tracking.css';
import './Page.css';
import { fetchTrackingDetails } from '../services/api';

const Tracking = () => {
  const [trackingNumber, setTrackingNumber] = useState('');
  const [trackingResult, setTrackingResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleTrackSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    setError(null);
    setTrackingResult(null);

    const cleanNum = trackingNumber.trim();
    if (!cleanNum) {
      setError('Please enter your tracking / consignment number.');
      return;
    }

    setLoading(true);

    try {
      const data = await fetchTrackingDetails(cleanNum);
      setTrackingResult(data);
    } catch (err) {
      setError(err.message || 'No tracking information found for this consignment number.');
      setTrackingResult(null);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPOD = () => {
    if (trackingResult?.podImage) {
      try {
        if (typeof trackingResult.podImage === 'string' && trackingResult.podImage.startsWith('data:image')) {
          const base64Data = trackingResult.podImage.split(',')[1] || trackingResult.podImage;
          const byteCharacters = atob(base64Data);
          const byteNumbers = new Array(byteCharacters.length);
          for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
          }
          const byteArray = new Uint8Array(byteNumbers);
          const blob = new Blob([byteArray], { type: 'image/jpeg' });
          
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `POD_${trackingResult.number || 'tracking'}.jpg`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
        } else if (typeof trackingResult.podImage === 'string' && trackingResult.podImage.startsWith('http')) {
          const link = document.createElement('a');
          link.href = trackingResult.podImage;
          link.download = `POD_${trackingResult.number || 'tracking'}.jpg`;
          link.target = '_blank';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }
      } catch (error) {
        console.error('Error downloading POD image:', error);
        alert('Failed to download POD image.');
      }
    }
  };

  const getStatusBadgeClass = (status) => {
    const s = (status || '').toLowerCase();
    if (s.includes('deliver') || s.includes('success')) return 'badge-delivered';
    if (s.includes('out') || s.includes('transit') || s.includes('reach') || s.includes('depart') || s.includes('hub')) return 'badge-transit';
    if (s.includes('fail') || s.includes('cancel') || s.includes('rto') || s.includes('undeliver')) return 'badge-danger';
    return 'badge-pending';
  };

  return (
    <div className="track-page">
      <div className="track-page-content">
        <div className="container">
          <div className="tracking-section">
            <div className="tracking-form-container">
              <h2>Track Your Shipment</h2>
              <p>Enter your consignment or AWB tracking number to get real-time status and complete checkpoint history.</p>

              {error && (
                <div className="tracking-alert-error">
                  <span className="alert-icon">⚠️</span>
                  <div>
                    <strong>Tracking Notice:</strong>
                    <div>{error}</div>
                  </div>
                </div>
              )}

              <form onSubmit={handleTrackSubmit} className="tracking-form">
                <input
                  type="text"
                  placeholder="Enter Consignment / AWB / Tracking Number"
                  value={trackingNumber}
                  onChange={(e) => setTrackingNumber(e.target.value)}
                  className="tracking-input"
                  required
                  disabled={loading}
                />
                <button type="submit" className="btn-primary" disabled={loading}>
                  {loading ? 'Tracking...' : 'Track'}
                </button>
              </form>
            </div>

            {/* Tracking Result View */}
            {trackingResult && (
              <div className="tracking-result">
                {/* Header Bar */}
                <div className="tracking-header-bar">
                  <h2>WORLD WIDE COURIER CARGO TRACKING SYSTEM</h2>
                </div>

                <div className="tracking-number-bar">
                  <span>Tracking No. #{trackingResult.number}</span>
                  <span className="tracking-carrier-tag">Carrier: World Wide Courier Cargo</span>
                </div>

                {/* Shipment Top Details */}
                <div className="tracking-header">
                  <div>
                    <h3>Shipment Overview</h3>
                    <p className="tracking-overview-sub">Real-time status updates and checkpoint records</p>
                  </div>
                  <div className="tracking-number-display">
                    <span className="tracking-label">Current Status</span>
                    <span className={`status-badge ${getStatusBadgeClass(trackingResult.status)}`}>
                      {trackingResult.status}
                    </span>
                  </div>
                </div>

                {/* Information Grid */}
                <div className="tracking-info-grid">
                  <div className="info-card">
                    <div className="info-label">Consignment / AWB No</div>
                    <div className="info-value">{trackingResult.number}</div>
                  </div>

                  <div className="info-card">
                    <div className="info-label">Carrier</div>
                    <div className="info-value">World Wide Courier Cargo</div>
                  </div>

                  <div className="info-card">
                    <div className="info-label">Current Status</div>
                    <div className="info-value">
                      <span className={`status-badge-inline ${getStatusBadgeClass(trackingResult.status)}`}>
                        {trackingResult.status}
                      </span>
                    </div>
                  </div>

                  {trackingResult.bookingDate && (
                    <div className="info-card">
                      <div className="info-label">Booking Date</div>
                      <div className="info-value">{trackingResult.bookingDate}</div>
                    </div>
                  )}

                  <div className="info-card">
                    <div className="info-label">Origin</div>
                    <div className="info-value">{trackingResult.origin || 'N/A'}</div>
                  </div>

                  <div className="info-card">
                    <div className="info-label">Destination</div>
                    <div className="info-value">{trackingResult.destination || 'N/A'}</div>
                  </div>

                  <div className="info-card">
                    <div className="info-label">Current Location</div>
                    <div className="info-value">{trackingResult.currentLocation || 'In Transit'}</div>
                  </div>

                  {trackingResult.consignorName && trackingResult.consignorName !== 'N/A' && (
                    <div className="info-card">
                      <div className="info-label">Consignor (Shipper)</div>
                      <div className="info-value">{trackingResult.consignorName}</div>
                      {trackingResult.consignorCity && (
                        <div className="info-subtext">
                          📍 {trackingResult.consignorCity}{trackingResult.consignorPin ? ` - ${trackingResult.consignorPin}` : ''}
                        </div>
                      )}
                    </div>
                  )}

                  {trackingResult.consignee && trackingResult.consignee !== 'N/A' && (
                    <div className="info-card">
                      <div className="info-label">Consignee (Receiver)</div>
                      <div className="info-value">{trackingResult.consignee}</div>
                      {trackingResult.consigneeCity && (
                        <div className="info-subtext">
                          📍 {trackingResult.consigneeCity}{trackingResult.consigneePin ? ` - ${trackingResult.consigneePin}` : ''}
                        </div>
                      )}
                    </div>
                  )}

                  {trackingResult.forwardPartner && (
                    <div className="info-card">
                      <div className="info-label">Delivery Partner</div>
                      <div className="info-value">{trackingResult.forwardPartner}</div>
                    </div>
                  )}

                  {trackingResult.forwardNo && (
                    <div className="info-card">
                      <div className="info-label">Partner AWB No</div>
                      <div className="info-value">{trackingResult.forwardNo}</div>
                    </div>
                  )}

                  {trackingResult.mode && (
                    <div className="info-card">
                      <div className="info-label">Mode / Service</div>
                      <div className="info-value">{trackingResult.mode}</div>
                    </div>
                  )}

                  {(trackingResult.noOfBox || trackingResult.weight) && (
                    <div className="info-card">
                      <div className="info-label">Pieces / Weight</div>
                      <div className="info-value">
                        {trackingResult.noOfBox || '1'} pcs / {trackingResult.weight || 'N/A'}
                      </div>
                    </div>
                  )}

                  <div className="info-card">
                    <div className="info-label">Last Updated Date</div>
                    <div className="info-value">{trackingResult.statusDate || trackingResult.estimatedDelivery || 'N/A'}</div>
                  </div>

                  {trackingResult.statusTime && (
                    <div className="info-card">
                      <div className="info-label">Last Updated Time</div>
                      <div className="info-value">{trackingResult.statusTime}</div>
                    </div>
                  )}

                  {trackingResult.statusRemark && (
                    <div className="info-card status-remark-card">
                      <div className="info-label">Latest Activity</div>
                      <div className="info-value remark-value">{trackingResult.statusRemark}</div>
                    </div>
                  )}

                  {/* Proof of Delivery Image if present */}
                  {trackingResult.podImage && (
                    <div className="info-card pod-card">
                      <div className="info-label">Proof of Delivery (POD)</div>
                      <div className="pod-image-container">
                        {typeof trackingResult.podImage === 'string' &&
                        (trackingResult.podImage.startsWith('data:image') || trackingResult.podImage.startsWith('http')) ? (
                          <>
                            <img
                              src={trackingResult.podImage}
                              alt="Proof of Delivery"
                              className="pod-image"
                              onError={(e) => {
                                e.target.style.display = 'none';
                                const errEl = e.target.nextSibling;
                                if (errEl) errEl.style.display = 'block';
                              }}
                            />
                            <div style={{ display: 'none', padding: '10px', textAlign: 'center', color: '#c33' }}>
                              Failed to load POD image
                            </div>
                            <button
                              type="button"
                              onClick={handleDownloadPOD}
                              className="download-pod-btn"
                              title="Download POD Image"
                            >
                              ⬇ Download POD
                            </button>
                          </>
                        ) : (
                          <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
                            POD Image available
                            <br />
                            <button
                              type="button"
                              onClick={handleDownloadPOD}
                              className="download-pod-btn"
                              style={{ marginTop: '10px' }}
                            >
                              ⬇ Download POD
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Shipment Checkpoint History Timeline */}
                <div className="tracking-history-container">
                  <div className="history-header">
                    <h4>Tracking History & Checkpoints</h4>
                    <span className="history-count">
                      {(trackingResult.history || []).length} Updates Recorded
                    </span>
                  </div>

                  {trackingResult.history && trackingResult.history.length > 0 ? (
                    <div className="timeline-wrapper">
                      {trackingResult.history.map((item, index) => (
                        <div key={index} className={`timeline-node ${index === 0 ? 'latest-node' : ''}`}>
                          <div className="timeline-marker">
                            <span className="marker-dot"></span>
                            {index !== trackingResult.history.length - 1 && <span className="marker-line"></span>}
                          </div>

                          <div className="timeline-content">
                            <div className="timeline-header-row">
                              <span className="timeline-status-badge">
                                {item.status}
                              </span>
                              <div className="timeline-timestamp">
                                {item.date && <span className="timestamp-date">{item.date}</span>}
                                {item.time && <span className="timestamp-time">{item.time}</span>}
                              </div>
                            </div>

                            {item.location && (
                              <div className="timeline-location">
                                <span className="location-pin">📍</span> {item.location}
                              </div>
                            )}

                            {item.remark && (
                              <p className="timeline-remark">{item.remark}</p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="timeline-empty-state">
                      <p>No intermediate checkpoints recorded yet for this consignment.</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Tracking;
